import { useBunja } from 'bunja/react';
import { addWeeks } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { ReactNode } from 'react';
import { useEffectOnce } from 'react-use';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal';
import { Toggle } from '@flipster/react/design-system/toggle';
import ArrowPathIcon from '@flipster/react/icon/generated/ArrowPathIcon';
import PipIcon from '@flipster/react/icon/generated/PipIcon';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { PipEvent } from '@flipster/utils/segment/events/pip';
import { TradeModeEvent } from '@flipster/utils/segment/events/trade-mode';

import { pipModeConfigBunja } from '../../_pip/bunja';
import { isPipSupported, showPipNotSupportedAlert } from '../../_pip/pip';
import { tradeLayoutBunja } from '../../trade/_misc/layout/state';
import { useDefaultLeverageMarginMode } from '../../trade/_misc/order-form/perpetual/modals/default-leverage-margin-mode-button';
import TradeSettingItem from './TradeSettingItem';
import { PipModeReleasedDate } from './constants';
import { SettingType } from './types';

interface Setting {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  type: SettingType;
  releasedDate?: Date;
  action?: ReactNode;
}

interface Props {
  onSelectType: (type: SettingType) => void;
}

const TradeSettings = ({ onSelectType }: Props) => {
  const { pipModeConfigAtom } = useBunja(pipModeConfigBunja);
  const [pipModeConfig, setPipModeConfig] = useAtom(pipModeConfigAtom);
  const [landedFeatures, setLandedFeatures] = useLocalStorage({
    key: 'flipster.configs.v1.trade_settings_landed_features',
    defaultValue: [],
  });
  const { isCustomizedAtom, resetLayout } = useBunja(tradeLayoutBunja);
  const isCustomized = useAtomValue(isCustomizedAtom);
  const { defaultLeverage, defaultMarginMode } = useDefaultLeverageMarginMode();

  useEffectOnce(() => {
    segment.track(TradeModeEvent.TradeSettingsView);
  });

  const Settings: Setting[] = [
    {
      icon: <PipIcon size={24} />,
      title: <FormattedMessage id='Account.r250401.Menu_pip_mode' />,
      description: (
        <FormattedMessage id='Assets.r251027.Picture_in_picture_mode_lets_you_keep_chart_position_info_desc' />
      ),
      type: 'pipMode' as const,
      releasedDate: PipModeReleasedDate,
      action: (
        <Toggle
          selected={pipModeConfig}
          onChange={() => {
            segment.track(PipEvent.PipModeToggleClick, {
              action: pipModeConfig ? 'turn_off' : 'turn_on',
              event_location: 'trade_settings_modal',
            });
            if (isPipSupported) {
              setPipModeConfig(!pipModeConfig);
            } else {
              showPipNotSupportedAlert();
            }
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id='Account.r250401.Menu_position_modes' />,
      description: <FormattedMessage id='Assets.r251027.Select_to_trade_futures_multi_one_way' />,
      type: 'positionMode' as const,
    },
    {
      title: (
        <div className={stack({ textAlign: 'left', gap: 2 })}>
          <div>
            <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode' />
          </div>
          <div className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-4' })}>
            <FormattedMessage id='Trade.r260528.Currently_set_to' /> {defaultLeverage}x, {defaultMarginMode}
          </div>
        </div>
      ),
      description: <FormattedMessage id='Trade.r260528.Default_leverage_margin_mode_description' />,
      type: 'defaultLeverageMarginMode' as const,
    },
    {
      title: <FormattedMessage id='Account.r250401.Menu_set_displayed_leverage' />,
      description: <FormattedMessage id='Assets.r251027.Select_to_display_position_or_effective_leverage' />,
      type: 'displayedLeverage' as const,
    },

    {
      title: <FormattedMessage id='Assets.r251103.Set_displayed_pnl' />,
      description: <FormattedMessage id='Assets.r251103.Select_display_unrealized_net_pnl' />,
      type: 'displayedPnl' as const,
    },
    {
      title: <FormattedMessage id='Assets.r251027.Learn_how_to_place_your_order' />,
      type: 'orderTutorial' as const,
    },
    {
      title: <FormattedMessage id='Trade.r260313.Layout_setting' />,
      type: 'layoutSetting' as const,
      action: (
        <Button
          hierarchy='secondary'
          color='neutral'
          size='small'
          icon={<ArrowPathIcon />}
          label={<FormattedMessage id='Trade.r260313.Reset_to_default_layout' />}
          onClick={() => resetLayout()}
          disabled={!isCustomized}
        />
      ),
    },
  ];

  return (
    <>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Assets.r251027.Trade_settings' />} />
      </Modal.Header>
      <Modal.Content>
        <ul className={flex({ direction: 'column', gap: 48, pb: 32 })}>
          {Settings.map(({ icon, title, description, type, releasedDate, action }, index) => {
            const isBeenTwoWeeks = releasedDate == null || new Date() > addWeeks(releasedDate, 2);
            const neverMetNewFeature = !isBeenTwoWeeks && !landedFeatures.includes(type);

            return (
              <li key={type}>
                {action != null ? (
                  <TradeSettingItem
                    icon={icon}
                    title={title}
                    description={description}
                    action={action}
                    showNewIcon={neverMetNewFeature}
                  />
                ) : (
                  <button
                    type='button'
                    className={css({ w: '100%' })}
                    onClick={() => {
                      onSelectType(type);
                      if (neverMetNewFeature) {
                        setLandedFeatures((prev) => [...prev, type]);
                      }
                      segment.track(TradeModeEvent.TradeSettingsSelectClick, {
                        list_order: index,
                        is_new: neverMetNewFeature,
                        menu_name: type,
                      });
                    }}
                  >
                    <TradeSettingItem
                      icon={icon}
                      title={title}
                      description={description}
                      showNewIcon={neverMetNewFeature}
                    />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </Modal.Content>
    </>
  );
};

export default TradeSettings;
